import { APP_TIMEZONE, APP_TIMEZONE_LABEL } from "@/config/timezone";
import type {
	BackupCause,
	CostWarningDto,
	ProviderBadgeDto,
	VoiceWarningKind,
} from "@/types/ai-assistant-failover-health";

const SLOT_VENDOR: Record<string, string> = {
	chat: "Claude",
	chat_backup: "OpenAI",
};

const CAUSE_TEXT: Record<BackupCause, string> = {
	quota_exhausted: "kuota/saldo habis",
	unavailable: "gangguan",
	auth_rejected: "kunci ditolak",
};

const REASON_TEXT: Record<string, string> = {
	...CAUSE_TEXT,
	circuit_open: "profil Utama sedang dilewati (sirkuit terbuka)",
};

const SLOT_LABEL: Record<string, string> = {
	chat: "Chat (Claude)",
	chat_backup: "Chat cadangan (OpenAI)",
};

export const slotLabel = (slot: string): string => SLOT_LABEL[slot] ?? slot;
export const reasonText = (reason: string): string =>
	REASON_TEXT[reason] ?? reason;
export const vendorOf = (slot: string): string => SLOT_VENDOR[slot] ?? slot;

const timeFormat = new Intl.DateTimeFormat("id-ID", {
	timeZone: APP_TIMEZONE,
	day: "2-digit",
	month: "short",
	hour: "2-digit",
	minute: "2-digit",
	hour12: false,
});

/** Waktu dalam WITA, mis. "20 Okt, 08.30 WITA". */
export function formatWita(iso: string): string {
	return `${timeFormat.format(new Date(iso))} ${APP_TIMEZONE_LABEL}`;
}

export interface BadgeView {
	label: string;
	color: "gray" | "green" | "yellow" | "red";
	detail: string;
}

/** Teks & warna lencana status penyedia dari DTO server. */
export function badgeView(
	badge: ProviderBadgeDto,
	backupSlot: string,
): BadgeView {
	switch (badge.state) {
		case "neutral":
			return {
				label: "Pindah otomatis mati",
				color: "gray",
				detail:
					badge.reason === "failover_off"
						? "Sakelar pindah otomatis mati, jadi status penyedia tidak dipantau."
						: "Profil Cadangan belum siap, jadi pindah otomatis tidak akan terjadi.",
			};
		case "primary_active":
			return {
				label: "Utama aktif",
				color: "green",
				detail: "Giliran dijawab profil Utama.",
			};
		case "using_backup": {
			const since = badge.since ? ` sejak ${formatWita(badge.since)}` : "";
			return {
				label: "Memakai cadangan",
				color: "yellow",
				detail: `Giliran dijawab ${vendorOf(backupSlot)}${since}; sebab: ${CAUSE_TEXT[badge.cause]}.`,
			};
		}
		case "both_down":
			return {
				label: "Keduanya bermasalah",
				color: "red",
				detail:
					"Profil Utama dan Cadangan sama-sama sedang dilewati; giliran chat bisa gagal.",
			};
	}
}

/** Kalimat spanduk biaya (P-10): vendor Cadangan + pemicu yang terlampaui. */
export function costWarningLines(
	w: CostWarningDto,
	backupSlot: string,
): { title: string; lines: string[] } {
	const lines: string[] = [];
	if (w.triggers.includes("duration") && w.durationMinutes !== null)
		lines.push(`Cadangan dipakai terus selama ${w.durationMinutes} menit.`);
	if (w.triggers.includes("share"))
		lines.push(
			`${w.sharePercent}% giliran chat hari ini (${w.backupTurns} dari ${w.totalTurns}) lewat Cadangan.`,
		);
	return {
		title: `Pemakaian bergeser ke ${vendorOf(backupSlot)}; periksa plafon/tarif`,
		lines,
	};
}

const VOICE_TEXT: Record<VoiceWarningKind, string> = {
	slot_not_ready:
		"Suara (GPT-Live OpenAI) tidak punya cadangan, dan slot Suara belum siap. Lengkapi kredensialnya.",
	openai_trouble:
		"Suara (GPT-Live OpenAI) tidak punya cadangan, dan profil OpenAI sedang bermasalah. Suara bisa tidak tersedia sampai OpenAI pulih.",
};

export const voiceWarningText = (kind: VoiceWarningKind): string =>
	VOICE_TEXT[kind];
